// OtsLaunch.js
//
// End-user launch page for a released Off-the-Shelf (OTS) pipeline. Mirrors the
// BYOP launch UI (BYOPWorkflow.js) — typed parameter fields with defaults +
// input files dragged from the user's library — but without the source-code
// editor (OTS pipelines are platform-managed). On launch it POSTs to /ots/launch,
// which runs the pipeline under the user as a per-user clone; results then appear
// in the user's analyses exactly like a BYOP run.
import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Modal from "react-modal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import "./css/FileUpload.css";
import "./css/WorkflowDetails.css";
import API_BASE from "./config";
import Pagination from "./components/Pagination";
import { normalizePrograms } from "./utils/normalizePrograms";
import { effectiveState as evalLaunchRules } from "./utils/launchRules";
import { AuthContext } from "./AuthContext";

const FILES_PER_PAGE = 15;

const OtsLaunch = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // ots_pipelines.id
    const { userData } = useContext(AuthContext);
    const cognitoUserId = userData && userData.cognitoUserId;

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [paramValues, setParamValues] = useState({});
    const [assigned, setAssigned] = useState({}); // { inputName: [{id, name}] }
    const [showValidation, setShowValidation] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [launchResult, setLaunchResult] = useState("");

    // user file library
    const [userFiles, setUserFiles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [selected, setSelected] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/ots-pipelines/${id}`);
                if (cancelled) return;
                setData(res.data);
                const init = {};
                (res.data.parameters || []).forEach((p) => {
                    if (p.type === "boolean") init[p.name] = p.default === "true" || p.default === true;
                    else if (p.type === "list") init[p.name] = p.default ? String(p.default).split(",").map((s) => s.trim()).filter(Boolean) : [];
                    else init[p.name] = p.default ?? "";
                });
                setParamValues(init);
            } catch (e) {
                if (!cancelled) setError("Could not load this pipeline.");
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        if (!cognitoUserId) return;
        (async () => {
            try {
                const idToken = localStorage.getItem("idToken");
                const res = await fetch(`${API_BASE}/user-files`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({ cognitoUserId }),
                });
                const d = await res.json();
                setUserFiles(
                    (d.files || []).map((f) => ({
                        id: f.id,
                        name: f.filename,
                        project: f.original_project || "",
                        path: f.s3_folder_path || "",
                        created: f.created_on
                            ? new Date(f.created_on).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                            : "",
                    }))
                );
            } catch (e) {
                console.error("Error fetching user files:", e);
            }
        })();
    }, [cognitoUserId]);

    // Apply any rule-forced parameter values (set_value) into the live form, so a
    // controlling selection (e.g. aligner=cellranger) locks the forced field
    // (protocol=auto) and that value is what gets submitted. Guarded to only write
    // when the value actually differs, so it settles in one pass (no loop).
    useEffect(() => {
        if (!data) return;
        const rules = Array.isArray(data.launch_rules) ? data.launch_rules : [];
        const st = evalLaunchRules(rules, paramValues);
        const updates = {};
        Object.entries(st).forEach(([name, cfg]) => {
            if (cfg.forcedValue !== undefined && String(paramValues[name] ?? "") !== String(cfg.forcedValue)) {
                updates[name] = cfg.forcedValue;
            }
        });
        if (Object.keys(updates).length) setParamValues((prev) => ({ ...prev, ...updates }));
    }, [data, paramValues]);

    const sortedFiles = useMemo(() => {
        if (!sortConfig.key) return userFiles;
        return [...userFiles].sort((a, b) => {
            const av = (a[sortConfig.key] || "").toString().toLowerCase();
            const bv = (b[sortConfig.key] || "").toString().toLowerCase();
            if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
            if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [userFiles, sortConfig]);

    const filteredFiles = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return sortedFiles;
        return sortedFiles.filter((f) =>
            ["name", "project", "path", "created"].some((k) => String(f[k] || "").toLowerCase().includes(q))
        );
    }, [sortedFiles, searchQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredFiles.length / FILES_PER_PAGE));
    const page = Math.min(currentPage, totalPages);
    const pagedFiles = filteredFiles.slice((page - 1) * FILES_PER_PAGE, page * FILES_PER_PAGE);

    if (error) return <p className="workflow-error-text">{error}</p>;
    if (!data) return <p className="workflow-loading-text">Loading pipeline…</p>;

    const inputs = Array.isArray(data.input_files) ? data.input_files : [];
    const params = Array.isArray(data.parameters) ? data.parameters : [];

    // Conditional launch rules — recomputed each render from the current values.
    const rules = Array.isArray(data.launch_rules) ? data.launch_rules : [];
    const ruleState = evalLaunchRules(rules, paramValues);
    const isHidden = (name) => !!(ruleState[name] && ruleState[name].hidden);

    const handleSort = (key) =>
        setSortConfig((prev) => (prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" }));

    const assignFiles = (inputName, files) =>
        setAssigned((prev) => {
            const cur = prev[inputName] || [];
            const seen = new Set(cur.map((f) => f.id));
            const merged = [...cur];
            files.forEach((f) => { if (f && f.id != null && !seen.has(f.id)) { merged.push(f); seen.add(f.id); } });
            return { ...prev, [inputName]: merged };
        });

    const removeFile = (inputName, fid) =>
        setAssigned((prev) => ({ ...prev, [inputName]: (prev[inputName] || []).filter((f) => f.id !== fid) }));

    const toggleSelect = (fid) =>
        setSelected((prev) => {
            const n = { ...prev };
            if (n[fid]) delete n[fid]; else n[fid] = true;
            return n;
        });
    const pageAllSelected = pagedFiles.length > 0 && pagedFiles.every((f) => selected[f.id]);
    const toggleSelectPage = () =>
        setSelected((prev) => {
            const n = { ...prev };
            if (pageAllSelected) pagedFiles.forEach((f) => delete n[f.id]);
            else pagedFiles.forEach((f) => { n[f.id] = true; });
            return n;
        });

    const onRowDragStart = (e, file) => {
        const payload = selected[file.id]
            ? userFiles.filter((f) => selected[f.id]).map((f) => ({ id: f.id, name: f.name }))
            : [{ id: file.id, name: file.name }];
        e.dataTransfer.setData("application/json", JSON.stringify(payload));
    };
    const onSectionDrop = (e, inputName) => {
        e.preventDefault();
        try {
            const arr = JSON.parse(e.dataTransfer.getData("application/json"));
            assignFiles(inputName, Array.isArray(arr) ? arr : [arr]);
        } catch (_) { /* ignore */ }
    };

    const SortHeader = ({ title, k }) => {
        let icon = <FaSort style={{ marginLeft: 6 }} />;
        if (sortConfig.key === k) icon = sortConfig.direction === "asc" ? <FaSortUp style={{ marginLeft: 6 }} /> : <FaSortDown style={{ marginLeft: 6 }} />;
        return (
            <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }} onClick={() => handleSort(k)}>
                {title}{icon}
            </span>
        );
    };

    const setParam = (name, value) => setParamValues((prev) => ({ ...prev, [name]: value }));

    const renderParamField = (p) => {
        const st = ruleState[p.name] || {};
        const forced = st.forcedValue !== undefined;         // set_value rule → locked field
        const val = forced ? st.forcedValue : paramValues[p.name];
        if (p.type === "boolean")
            return <input type="checkbox" disabled={forced} checked={forced ? String(st.forcedValue) === "true" : (val === true || val === "true")} onChange={(e) => setParam(p.name, e.target.checked)} />;
        if (p.type === "enum") {
            let opts = Array.from(new Set([
                ...(p.choices || []).map(String),
                ...(p.default != null && p.default !== "" ? [String(p.default)] : []),
                ...(val != null && val !== "" ? [String(val)] : []),
            ]));
            // allow_values rule → restrict the selectable options (keep current value visible).
            if (st.allowed) opts = opts.filter((o) => st.allowed.has(o) || o === String(val ?? ""));
            return (
                <select value={val ?? ""} disabled={forced} onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle}>
                    <option value="">-- Select --</option>
                    {opts.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            );
        }
        if (p.type === "list") {
            const sel = Array.isArray(val) ? val : (val ? String(val).split(",").map((s) => s.trim()).filter(Boolean) : []);
            const defToks = p.default ? String(p.default).split(",").map((s) => s.trim()).filter(Boolean) : [];
            const opts = Array.from(new Set([...(p.choices || []).map(String), ...defToks, ...sel.map(String)]));
            if (opts.length) {
                return (
                    <select multiple value={sel} onChange={(e) => setParam(p.name, Array.from(e.target.selectedOptions).map((o) => o.value))} style={{ ...fieldStyle, minHeight: 72 }}>
                        {opts.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                );
            }
            return <input type="text" value={sel.join(",")} placeholder="comma,separated,values" onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle} />;
        }
        if (p.type === "number")
            return <input type="number" value={val ?? ""} disabled={forced} onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle} />;
        return (
            <input type="text" value={val ?? ""} disabled={forced}
                placeholder={p.type === "path" ? "https://… or s3://…" : ""}
                onChange={(e) => setParam(p.name, e.target.value)} style={fieldStyle} />
        );
    };

    // ---- launch gating ----
    const validated = data.validated_final === "Y";
    // A field is required if its own flag says so OR a rule requires it; hidden fields are exempt.
    const inputRequired = (f) => !isHidden(f.name) && (f.required !== false || !!(ruleState[f.name] && ruleState[f.name].required));
    const paramRequired = (p) => !isHidden(p.name) && (!!p.required || !!(ruleState[p.name] && ruleState[p.name].required));
    const missingInputNames = inputs.filter((f) => inputRequired(f) && !((assigned[f.name] || []).length > 0)).map((f) => f.name);
    const missingParamNames = params
        .filter((p) => paramRequired(p) && p.type !== "boolean" && (paramValues[p.name] === "" || paramValues[p.name] == null))
        .map((p) => p.name);
    const isPathOk = (v) => { v = String(v || "").trim(); return /^https?:\/\/.+/i.test(v) || /^s3:\/\/[^/\s]+(\/.*)?$/i.test(v); };
    // Reason messages for every invalid param (path format OR a rule violation).
    const paramInvalidReasons = {};
    params.forEach((p) => {
        if (isHidden(p.name)) return;
        const raw = paramValues[p.name];
        const sv = Array.isArray(raw) ? raw.join(",") : (raw == null ? "" : String(raw));
        if (p.type === "path" && sv.trim() !== "" && !isPathOk(sv)) {
            paramInvalidReasons[p.name] = "must be an http(s):// or s3:// path";
        }
        const st = ruleState[p.name];
        if (st && sv !== "") {
            if (st.forcedValue !== undefined && sv !== String(st.forcedValue)) paramInvalidReasons[p.name] = `must be "${st.forcedValue}"`;
            else if (st.forbidden && st.forbidden.has(sv)) paramInvalidReasons[p.name] = `cannot be "${sv}"`;
            else if (st.allowed && !st.allowed.has(sv)) paramInvalidReasons[p.name] = `must be one of: ${[...st.allowed].join(", ")}`;
        }
    });
    const invalidParamNames = Object.keys(paramInvalidReasons);
    const canLaunch = validated && missingInputNames.length === 0 && missingParamNames.length === 0 && invalidParamNames.length === 0;

    const previewCommand = (() => {
        const main = String(data.main_script || "main.nf").split("/").pop();
        const parts = [`nextflow run ${main}`];
        inputs.forEach((f) => {
            if (isHidden(f.name)) return;
            const names = (assigned[f.name] || []).map((x) => x.name);
            if (names.length) parts.push(`${f.flag} ${names.join(",")}`);
        });
        params.forEach((p) => {
            if (isHidden(p.name)) return;
            const st = ruleState[p.name] || {};
            const always = !!p.alwaysInclude; // "Add to Command" → force inclusion
            const v = st.forcedValue !== undefined ? st.forcedValue : paramValues[p.name];
            if (p.type === "boolean") { if (always || v === true || v === "true") parts.push(p.flag); return; }
            const sv = Array.isArray(v) ? v.join(",") : (v == null ? "" : String(v));
            if (sv.trim() === "") return;
            if (!always && p.default != null && String(p.default) === sv) return; // unchanged from default → pipeline uses its typed default
            parts.push(`${p.flag} ${/\s/.test(sv) ? `"${sv}"` : sv}`);
        });
        return parts.join(" ");
    })();

    const openLaunch = () => {
        if (!validated) return;
        if (missingInputNames.length || missingParamNames.length || invalidParamNames.length) {
            setShowValidation(true);
            return;
        }
        setShowValidation(false);
        setModalOpen(true);
    };

    const confirmLaunch = async () => {
        try {
            const inputFileIds = {};
            inputs.forEach((f) => { inputFileIds[f.name] = (assigned[f.name] || []).map((x) => x.id); });
            const dataIds = (assigned["__data__"] || []).map((x) => x.id);
            if (dataIds.length) inputFileIds["__data__"] = dataIds;
            await axios.post(
                `${API_BASE}/ots/launch`,
                { otsPipelineId: id, paramValues, inputFileIds },
                { withCredentials: true }
            );
            setModalOpen(false);
            navigate("/analyses");
        } catch (e) {
            setLaunchResult("Launch failed: " + (e.response?.data?.error || e.message));
        }
    };

    return (
        <div className="workflow-details-page">
            <div className="workflow-header">
                <h3 className="workflow-title">
                    {data.short_name} <span style={{ fontSize: 13, color: "#0369a1" }}>· 🧬 Off-the-Shelf pipeline</span>
                </h3>
                {data.github_url && (
                    <a href={data.github_url} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontSize: 13 }}>
                        {data.github_url}
                    </a>
                )}
            </div>

            <section className="workflow-section">
                <h4 className="workflow-section-title">Workflow Summary</h4>
                {data.workflow_summary ? (
                    <div className="workflow-summary">{data.workflow_summary}</div>
                ) : (
                    <p className="workflow-muted-text">No workflow summary available.</p>
                )}
            </section>

            {data.instructions && String(data.instructions).trim() && (
                <section className="workflow-section">
                    <details open>
                        <summary className="workflow-section-title" style={{ cursor: "pointer", listStyle: "revert", marginBottom: 0 }}>
                            Documentation
                        </summary>
                        <div className="ots-instructions" style={{ marginTop: 12, fontSize: "0.92rem", lineHeight: 1.6, color: "#334155" }}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                urlTransform={(url) => {
                                    if (!url) return url;
                                    // Repo-relative doc asset (e.g. docs/images/pipeline.svg) → resolve against
                                    // this pipeline's imported folder, served statically under /workflow_files.
                                    const rel = data.nextflow_path
                                        ? data.nextflow_path.replace(/\/[^/]*$/, "").replace(/^\/+/, "")
                                        : "";
                                    if (rel && !/^[a-z][a-z0-9+.-]*:/i.test(url) && !url.startsWith("/") && !url.startsWith("#")) {
                                        return `${API_BASE}/${rel}/${url}`;
                                    }
                                    return url;
                                }}
                            >{data.instructions}</ReactMarkdown>
                        </div>
                    </details>
                </section>
            )}

            <section className="workflow-section">
                <h4 className="workflow-section-title">Programs Used in Analysis</h4>
                {(() => {
                    const steps = normalizePrograms(data.containers_list);
                    if (!steps.length) return <p className="workflow-muted-text">No programs listed for this workflow.</p>;
                    return (
                        <table className="workflow-programs-table">
                            <thead>
                                <tr>
                                    <th className="workflow-programs-header-step">STEP</th>
                                    <th className="workflow-programs-header-programs">PROGRAMS USED</th>
                                </tr>
                            </thead>
                            <tbody>
                                {steps.map((step, i) => (
                                    <tr key={i}>
                                        <td className="workflow-programs-step-cell">{step.name}</td>
                                        <td className="workflow-programs-programs-cell">
                                            {step.programs.map((p, j) => (
                                                <div key={j} className="workflow-program-entry">
                                                    <strong>{p.name}</strong>
                                                    {p.version && ` (v${p.version})`}
                                                    {p.default_parameters && (
                                                        <div className="workflow-program-default-params">Default params: {p.default_parameters}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
            </section>

            {/* PARAMETERS */}
            <section className="workflow-section">
                <h4 className="workflow-section-title">Set Parameters</h4>
                {params.length === 0 ? (
                    <p className="workflow-muted-text">This pipeline has no configurable parameters.</p>
                ) : (
                    <table className="workflow-programs-table">
                        <thead>
                            <tr>
                                <th className="workflow-programs-header-step">PARAMETER</th>
                                <th className="workflow-programs-header-programs">VALUE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {params.filter((p) => !isHidden(p.name)).map((p, i) => {
                                const st = ruleState[p.name] || {};
                                const ruleRequired = !p.required && !!st.required; // required by a rule, not statically
                                return (
                                <tr
                                    key={i}
                                    style={showValidation && (missingParamNames.includes(p.name) || invalidParamNames.includes(p.name)) ? { background: "#fef2f2", outline: "2px solid #ef4444" } : undefined}
                                >
                                    <td className="workflow-programs-step-cell" style={{ verticalAlign: "top" }}>
                                        <div><code>{p.flag}</code>
                                            {(p.required || ruleRequired) && !showValidation ? <span style={{ color: "#ef4444" }}> *</span> : null}
                                            {showValidation && missingParamNames.includes(p.name) ? <span style={{ color: "#ef4444" }}> — required</span> : null}
                                            {showValidation && invalidParamNames.includes(p.name) ? <span style={{ color: "#ef4444" }}> — {paramInvalidReasons[p.name]}</span> : null}
                                            {st.forcedValue !== undefined ? <span style={{ color: "#0369a1", fontSize: 11 }}> — set by configuration</span> : null}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>{p.type}{ruleRequired ? " · required for this configuration" : ""}</div>
                                        {p.description ? <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{p.description}</div> : null}
                                    </td>
                                    <td className="workflow-programs-programs-cell">
                                        {renderParamField(p)}
                                        {p.default !== "" && p.default != null && (
                                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>default: {String(p.default)}</div>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>

            {/* INPUT FILES — drag from the library below into each section */}
            <div className="workflow-upload-section">
                <h4 className="workflow-upload-heading">Assign input files</h4>
                <p className="workflow-muted-text" style={{ marginTop: 0 }}>
                    Drag files from your library (right) into each input section. Tick multiple files and drag any ticked row to move them all at once.
                </p>

                <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <div style={{ flex: "1 1 50%", minWidth: 0 }}>
                {inputs.length === 0 ? (
                    <p className="workflow-muted-text">This pipeline requires no input files.</p>
                ) : (
                    inputs.filter((input) => !isHidden(input.name)).map((input, i) => {
                        const files = assigned[input.name] || [];
                        const inputRuleRequired = !!(ruleState[input.name] && ruleState[input.name].required);
                        return (
                            <div className="upload-section" key={i}>
                                <label className="upload-section-label">
                                    {input.name} <code>{input.flag}</code>
                                    {input.extensions?.length ? (
                                        <span className="workflow-muted-text"> &nbsp;({input.extensions.join(", ")})</span>
                                    ) : null}
                                    {inputRuleRequired ? <span style={{ color: "#ef4444" }}> * required for this configuration</span> : null}
                                </label>
                                {input.description ? (
                                    <p className="workflow-muted-text" style={{ margin: "2px 0 6px" }}>{input.description}</p>
                                ) : null}
                                <div
                                    className="upload-dropzone"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => onSectionDrop(e, input.name)}
                                    style={{
                                        minHeight: 60,
                                        ...(showValidation && missingInputNames.includes(input.name)
                                            ? { border: "2px solid #ef4444", background: "#fef2f2" }
                                            : {}),
                                    }}
                                >
                                    {files.length === 0 ? (
                                        <p className="upload-dropzone-text">Drop files here from your library.</p>
                                    ) : (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                            {files.map((f) => (
                                                <span key={f.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e0f2fe", color: "#0369a1", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}>
                                                    {f.name}
                                                    <button onClick={() => removeFile(input.name, f.id)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0369a1", fontWeight: "bold" }} title="Remove">×</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}

                {/* Static "Data" input — copied into the run folder but not added as a flag. */}
                <div className="upload-section">
                    <label className="upload-section-label">
                        Data <span className="workflow-muted-text">&nbsp;(raw data files — optional)</span>
                    </label>
                    <p className="workflow-muted-text" style={{ margin: "2px 0 6px" }}>
                        Drop any data files your pipeline or samplesheet references by name (e.g. FASTQ/BAM).
                        These are copied into the analysis folder alongside the pipeline so it can find them; they are not added to the run command.
                    </p>
                    <div className="upload-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => onSectionDrop(e, "__data__")} style={{ minHeight: 60 }}>
                        {(assigned["__data__"] || []).length === 0 ? (
                            <p className="upload-dropzone-text">Drop files here from your library.</p>
                        ) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {(assigned["__data__"] || []).map((f) => (
                                    <span key={f.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#e0f2fe", color: "#0369a1", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}>
                                        {f.name}
                                        <button onClick={() => removeFile("__data__", f.id)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0369a1", fontWeight: "bold" }} title="Remove">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                  </div>

                  {/* RIGHT: sticky file library (drag source) */}
                  <div style={{ flex: "1 1 50%", minWidth: 0, position: "sticky", top: 16, maxHeight: "calc(100vh - 140px)", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, background: "#fff" }}>
                <h4 className="workflow-section-title" style={{ marginTop: 0 }}>Your files</h4>
                <input type="text" placeholder="Search files (name, project, path, date)…" value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{ padding: 8, fontSize: 14, width: "100%", boxSizing: "border-box", marginBottom: 10, border: "1px solid #cbd5e1", borderRadius: 4 }} />
                {userFiles.length === 0 ? (
                    <p className="workflow-muted-text">No files in your library yet. Import or upload data first, then it will appear here.</p>
                ) : (
                    <>
                        <div style={{ overflowX: "auto" }}>
                        <table className="workflow-programs-table" style={{ minWidth: 640 }}>
                            <thead>
                                <tr>
                                    <th className="workflow-programs-header-step" style={{ width: 32 }}>
                                        <input type="checkbox" checked={pageAllSelected} onChange={toggleSelectPage} title="Select all on this page" />
                                    </th>
                                    <th className="workflow-programs-header-step"><SortHeader title="Filename" k="name" /></th>
                                    <th className="workflow-programs-header-programs"><SortHeader title="Project" k="project" /></th>
                                    <th className="workflow-programs-header-programs"><SortHeader title="S3 Path" k="path" /></th>
                                    <th className="workflow-programs-header-programs"><SortHeader title="Created" k="created" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedFiles.map((f) => (
                                    <tr key={f.id} draggable onDragStart={(e) => onRowDragStart(e, f)}
                                        style={{ cursor: "grab", background: selected[f.id] ? "#e0f2fe" : undefined }}
                                        title="Drag into an input section (drags all ticked files if this row is ticked)">
                                        <td className="workflow-programs-step-cell" style={{ width: 32 }}>
                                            <input type="checkbox" checked={!!selected[f.id]} onChange={() => toggleSelect(f.id)} />
                                        </td>
                                        <td className="workflow-programs-step-cell" style={{ whiteSpace: "nowrap" }}>⠿ {f.name}</td>
                                        <td className="workflow-programs-programs-cell">{f.project}</td>
                                        <td className="workflow-programs-programs-cell" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{f.path}</td>
                                        <td className="workflow-programs-programs-cell" style={{ whiteSpace: "nowrap" }}>{f.created}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Pagination page={page} totalPages={totalPages} onChange={setCurrentPage} />
                        {Object.keys(selected).length > 0 && (
                            <p className="workflow-muted-text" style={{ marginTop: 6 }}>
                                {Object.keys(selected).length} file(s) selected — drag any ticked row to add them all.
                            </p>
                        )}
                    </>
                )}
                  </div>
                </div>{/* end side-by-side */}

                {!validated && (
                    <div style={{ margin: "12px 0", padding: "10px 14px", borderRadius: 8, border: "1px solid #fcd34d", background: "#fffbeb", color: "#92400e", fontSize: 14 }}>
                        ⏳ This pipeline is not yet available to launch.
                    </div>
                )}

                {showValidation && !canLaunch && (
                    <div style={{ margin: "12px 0", padding: "10px 14px", borderRadius: 8, border: "1px solid #fca5a5", background: "#fef2f2", color: "#b91c1c", fontSize: 14 }}>
                        <strong>Can't launch yet — please complete the highlighted item(s):</strong>
                        <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                            {missingInputNames.map((n) => <li key={`i-${n}`}>Input file: <code>{n}</code> (drag at least one file into it)</li>)}
                            {missingParamNames.map((n) => <li key={`p-${n}`}>Parameter: <code>{n}</code> (enter a value)</li>)}
                            {invalidParamNames.map((n) => <li key={`v-${n}`}>Parameter: <code>{n}</code> ({paramInvalidReasons[n]})</li>)}
                        </ul>
                    </div>
                )}

                {validated && (
                    <div style={{ margin: "12px 0", padding: "12px 14px", borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e40af", marginBottom: 6 }}>
                            {canLaunch ? "Final command to be run" : "Command preview (updates as you fill in the fields)"}
                        </div>
                        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12.5, color: "#0f172a" }}>{previewCommand}</pre>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>Platform flags (config, work-dir, weblog, output) are added automatically at launch.</div>
                    </div>
                )}

                <div className="workflow-launch-container">
                    <button onClick={openLaunch} className="workflow-launch-button" disabled={!validated}
                        title={!validated ? "This pipeline can't be launched yet." : undefined}>
                        LAUNCH ANALYSIS
                    </button>
                </div>
            </div>

            <Modal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)} className="workflow-modal" overlayClassName="workflow-modal-overlay" ariaHideApp={false}>
                <h2 className="workflow-modal-title">Launch Analysis</h2>
                {!launchResult ? (
                    <>
                        <p className="workflow-modal-body">
                            This will run the pipeline on the cloud with the selected files and parameters. Proceed?
                        </p>
                        <div className="workflow-modal-buttons">
                            <button onClick={confirmLaunch} className="workflow-modal-button workflow-modal-button-confirm">YES</button>
                            <button onClick={() => setModalOpen(false)} className="workflow-modal-button workflow-modal-button-cancel">NO</button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="workflow-modal-body">{launchResult}</p>
                        <div className="workflow-modal-buttons">
                            <button onClick={() => { setModalOpen(false); setLaunchResult(""); }} className="workflow-modal-button workflow-modal-button-cancel">CLOSE</button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

const fieldStyle = { padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, minWidth: 220, fontSize: 13 };

export default OtsLaunch;
